import { LevaPanel } from 'leva';
import { useExperimentsLevaStore } from '~/lib/experiments/levaStoreContext';

const LEVA_THEME = {
  colors: {
    elevation1: 'oklch(0.96 0.008 195)',
    elevation2: 'oklch(0.99 0.004 195)',
    elevation3: 'oklch(0.93 0.012 195)',
    accent1: 'oklch(0.55 0.09 195)',
    accent2: 'oklch(0.62 0.1 195)',
    accent3: 'oklch(0.7 0.11 195)',
    highlight1: 'oklch(0.45 0.03 20)',
    highlight2: 'oklch(0.3 0.03 20)',
    highlight3: 'oklch(0.2 0.03 20)',
  },
} as const;

/**
 * The single always-on controls panel for every experiment: it renders the shared leva store, so a
 * scene's controls and the tier controls (added by `TierProvider`) live in one panel with every
 * sub-folder collapsed by default. Visitors can expand a folder to play. Leva's root (`#leva__root`)
 * keeps the native cursor via the site cursor rules, so the custom marker stays visible over it.
 *
 * @param props.panelRef - Optional ref to the panel wrapper, used by scenes that pause their
 *   pointer interaction while the cursor is over the controls
 * @param props.collapsed - Start folded to the title bar, e.g. on mobile where the open panel covers the scene
 * @param props.belowBackLink - On mobile, drop below the fixed top-left `ExperienceBackLink`, which the panel's width would otherwise reach under
 * @param props.inline - Render in flow at full width with no title bar, e.g. inside the mobile `ExperienceDrawer`;
 *   it opts out of the drawer's swipe-to-dismiss so dragging a slider doesn't close the sheet
 * @returns The themed, non-draggable controls panel pinned to the top-right, or in flow when `inline`
 */
export function ExperimentsLevaPanel({ panelRef, collapsed = false, belowBackLink = false, inline = false }: { panelRef?: React.Ref<HTMLDivElement>; collapsed?: boolean; belowBackLink?: boolean; inline?: boolean }) {
  const store = useExperimentsLevaStore();

  if (inline) {
    return (
      <div ref={panelRef} data-cursor="native" data-base-ui-swipe-ignore="" className="w-full">
        <LevaPanel store={store} titleBar={false} fill flat theme={LEVA_THEME} />
      </div>
    );
  }

  return (
    <div ref={panelRef} data-cursor="native" className={`absolute right-4 z-30 w-72 ${belowBackLink ? 'top-21 md:top-4' : 'top-4'}`}>
      <LevaPanel
        store={store}
        titleBar={{ title: 'Controls', drag: false, filter: false }}
        collapsed={collapsed}
        fill={false}
        theme={LEVA_THEME}
      />
    </div>
  );
}
